import React, { useEffect, useState } from "react";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Button } from "@/components/ui/button";
import { useNavigate } from "react-router-dom";
import { useProviderCredentials } from "@/hooks/useProviderCredentials";
import { msUntilExpiry } from "@/utils/expiry";

// How long to snooze the reminder when user clicks "Remind me later"
const SNOOZE_DURATION_MS = 24 * 60 * 60 * 1000; // 24 hours
const SNOOZE_STORAGE_KEY = "credential-expiry-snooze-until";

/**
 * Display a dismiss-able banner when *any* cloud-provider credential has
 * expired (or will expire very soon). The banner appears below the main site
 * header and disappears when the user renews the credential or clicks
 * "Remind me tomorrow".
 */
const CredentialExpiryBanner: React.FC = () => {
  const navigate = useNavigate();
  const { data: credentials } = useProviderCredentials();
  const [dismissed, setDismissed] = useState(false);

  // Check if we should snooze
  const snoozeUntil = (() => {
    const raw = localStorage.getItem(SNOOZE_STORAGE_KEY);
    return raw ? parseInt(raw, 10) : 0;
  })();

  const isSnoozed = Date.now() < snoozeUntil;

  // Determine if there is at least one expired credential
  const hasExpired = (credentials || []).some((cred) => {
    if (!cred.last_validated_at) return false;
    return msUntilExpiry(cred.last_validated_at) <= 0;
  });

  const shouldShow = !dismissed && !isSnoozed && hasExpired;

  // Utility to start snooze
  const handleRemindTomorrow = () => {
    const until = Date.now() + SNOOZE_DURATION_MS;
    localStorage.setItem(SNOOZE_STORAGE_KEY, until.toString());
    setDismissed(true);
  };

  if (!shouldShow) return null;

  return (
    <Alert
      variant="destructive"
      className="rounded-none flex flex-col md:flex-row md:items-center gap-4 justify-between py-2 px-4"
    >
      <AlertDescription>
        Your cloud-provider credentials have expired. Data collection is paused
        until you renew.
      </AlertDescription>
      <div className="flex gap-2 shrink-0">
        <Button
          size="sm"
          className="bg-[#3CC858] hover:bg-[#34B04A] text-white"
          onClick={() => navigate("/settings?tab=cloud-providers")}
        >
          Renew now
        </Button>
        <Button variant="outline" size="sm" onClick={handleRemindTomorrow}>
          Remind me tomorrow
        </Button>
      </div>
    </Alert>
  );
};

export default CredentialExpiryBanner;
